import { type PrefilledDraftInput } from './PrefilledDraftInput'
import { memoizeAsync } from '@/utils/memoize-decorator'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { merge } from 'rxjs'
import { map, share, switchMap, tap } from 'rxjs/operators'
import { Id } from '@/infra/id'
import { toStream } from '@/utils/mobx-util'
import { type ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type {
  ListUserTeamMembersFn,
  GetCurrentMemberFn,
  GetTeamMemberFn,
} from '@/features/iam'
import type { NavigateFn } from '@/RootStore'
import { links } from '@/utils/link'
import {
  type DraftJobViewModel,
  type DraftJobViewModelFactory,
} from './DraftJobViewModel'
import type { GetDraftingMembersFn } from './drafting-members'
import { canSubmitDraftJob } from './draft-permissions'

/**
 * Job Creation Root View Model.
 * Responsible for managing instances of Job Creation.
 */
export class JobCreationRootViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Instances of editors.
   *
   * @private
   */
  private readonly _instances = new Map<Id, DraftJobViewModel>()

  // Separate from the lifecycle signal: some callers (call center, OCR
  // scoping) run `edit` without activating the root.
  private _editAbortController = new AbortController()

  /**
   * Current editor instance.
   */
  @observable accessor current: DraftJobViewModel

  /**
   * Current editor instance as a stream.
   */
  readonly current$ = toStream(() => this.current, true)

  /**
   * Emits a value when a Draft Job was created.
   */
  readonly jobCreated$

  /**
   * Emits the job when the edited job turns out not to be a draft.
   */
  readonly notADraft$

  /**
   * Emits a value when a Draft Job was deleted.
   */
  readonly jobDeleted$

  /**
   * Constructor.
   */
  constructor(
    private readonly _createDraftJobViewModel: DraftJobViewModelFactory,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getDraftingMembers: GetDraftingMembersFn,
    private readonly _getTeamMember: GetTeamMemberFn,
    private readonly _listUserTeamMembers: ListUserTeamMembersFn,
    private readonly _navigate: NavigateFn,
  ) {
    this.current = this._createEditor()

    this.jobCreated$ = this.current$.pipe(
      switchMap((editor) => editor.jobCreated$),
      share(),
    )

    this.notADraft$ = this.current$.pipe(
      switchMap((editor) => editor.notADraft$),
      share(),
    )

    this.jobDeleted$ = this.current$.pipe(
      switchMap((editor) => editor.jobDeleted$),
      share(),
    )

    this._lifecycle.manage(
      merge(
        this.jobCreated$.pipe(
          map((job) => links.editDraftJob(Id.assert(job.id))),
        ),
        this.jobDeleted$.pipe(map(() => links.jobs())),
        this.notADraft$.pipe(map((job) => links.job(Id.assert(job.id)))),
      ).pipe(tap((url) => this._navigate(url, { replace: true }))),
    )
  }

  /**
   * Whether the current user is allowed to start a draft.
   *
   * Checks permissions for teams as well. It is a prerequisite that
   * all the necessary info has been fetched which is
   * done with `fetchAccessInfo`
   */
  @computed
  get canDraft() {
    return this._getDraftingMembers().length > 0
  }

  /**
   * Whether we are allowed to submit the Draft that is currently loaded.
   */
  @computed
  get canSubmit() {
    return canSubmitDraftJob(
      this.current.job,
      this._getCurrentMember,
      this._getTeamMember,
    )
  }

  /**
   * Whether the current user is allowed to view the draft editor.
   */
  @computed
  get canViewEditor() {
    return this.canSubmit || this.canDraft
  }

  /**
   * Whether the user can edit draft jobs.
   */
  canEditDraftJobAsAdmin(workspaceId: Id) {
    return this._getCurrentMember(workspaceId).permissions.adminJobCreate
  }

  /**
   * Activates the root, which keeps the URL in sync with the draft being
   * edited.
   */
  @action.bound
  activate() {
    this._lifecycle.activate()
    // The route loader runs `edit` before the view mounts, so the editor it
    // prepared has to be reactivated when the view remounts.
    if (this.edit.resolved && !this.current.isFinished) {
      this.current.activate()
      // Deactivating un-readied the slices, and `edit` won't run again to
      // ready them.
      if (this.current.prepare.resolved) {
        this.current.revive()
      }
    }
  }

  /**
   * Deactivates the root and aborts any pending `edit`, which will
   * gracefully cancel.
   */
  @action.bound
  deactivate() {
    this._lifecycle.deactivate()
    this._editAbortController.abort()
    this.current.deactivate()
  }

  /**
   * Prepares the View Model with an existing draft, or by resetting
   * to prepare for a new draft.
   *
   * Will be called when:
   *   1. Loading an existing draft
   *   2. Starting a new draft
   *
   * Will NOT be called after a draft instance is created
   * in point 2 above, because at that point we already have that
   * draft's data loaded.
   *
   * @param jobId If specified, loads the Job
   * @param newDraft If creating a new draft, uses this context
   * @param isDisabled If specified, will be used to determine whether the fields from the editor are disabled
   */
  edit = task(
    async (
      jobId: Id,
      newDraft: PrefilledDraftInput,
      isDisabled?: () => boolean,
    ) => {
      // Start by fetching info required to determine whether we can view this screen.
      await this._fetchAccessInfo()

      // If we can't view the editor, exit early.
      if (!this.canViewEditor) {
        return this.current
      }

      // If the current editor is revivable, do so.
      if (this.current.isRevivable(jobId)) {
        this.current.activate()
        this.current.revive()
        return this.current
      }

      // Cancel any currently running edit/prepare tasks.
      this._editAbortController.abort()
      this._editAbortController = new AbortController()
      const signal = this._editAbortController.signal

      if (Id.some(jobId)) {
        // Check for an existing editor or create a new one.
        const existing =
          this.current.job.id === jobId
            ? this.current
            : this._instances.get(jobId)
        this._set(
          existing && !existing.isFinished
            ? existing
            : this._createEditor(isDisabled),
        )
      } else if (this.current.jobWasCreated || !newDraft.isEmpty) {
        // In this case, the current editor represents an existing draft
        // and we are trying to start a new one, so replace it.
        // On the other hand, if we go from "new" to "new", we don't
        // want to reset the state by trashing the current editor unless
        // we are prefilling stuff.
        this._set(this._createEditor(isDisabled))
      }

      // Preparing resets the editor's preparation state before activation
      // restarts its pipelines, and the slices need to be active to prepare.
      const preparing = this.current.prepare(jobId, newDraft, signal)
      this.current.activate()
      await preparing
      signal.throwIfAborted()
      return this.current
    },
  )

  /**
   * Fetches info required for making access decisions, once per session
   * unless it fails.
   *
   * @private
   */
  @memoizeAsync({ keySelector: () => '' })
  private async _fetchAccessInfo() {
    if (this._getDraftingMembers().length === 0) {
      return
    }

    await this._listUserTeamMembers({
      userPublicId: this._getCurrentMember().user.publicId,
    })
  }

  /**
   * Sets the editor. The previous one is kept for when its draft is edited
   * again, unless it has no draft or is finished.
   *
   * @param editor
   * @private
   */
  @action.bound
  private _set(editor: DraftJobViewModel) {
    if (editor === this.current) {
      return
    }

    // NOTE: We call deactivate here, but not activate,
    // since that gets called by `edit`.
    const previous = this.current
    previous.deactivate()
    if (previous.isFinished) {
      this._instances.delete(previous.job.id)
    } else if (previous.jobWasCreated) {
      this._instances.set(previous.job.id, previous)
    }

    this.current = editor
  }

  /**
   * Creates an editor.
   *
   * @private
   */
  private _createEditor(isDisabled?: () => boolean): DraftJobViewModel {
    return this._createDraftJobViewModel(
      (workspaceId) => this.canEditDraftJobAsAdmin(workspaceId),
      isDisabled ?? (() => false),
    )
  }
}
